// Scene 2 - Paket waehlen (Suche + Grid + "Mehr anzeigen").
// Das i auf der Kachel oeffnet die Artefaktliste MIT Deploy-Status, damit sich ein
// Paket pruefen laesst, ohne es auszuwaehlen und den Wizard weiterzuschalten.

import React, { useMemo, useRef, useState } from "react";
import Skeleton from "../../../components/Skeleton";
import { useLang } from "../i18n";
import { useTransportV2 } from "../context/TransportV2Context";
import { usePackageList } from "../hooks/usePackageList";
import { api } from "../../../utils/helpers";

const INITIAL_VISIBLE = 6;

// 2-Buchstaben-Monogramm wie instanceMark() in der alten Transport-App,
// hier in Signal-Orange (siehe .pkg-mark).
function pkgMark(name) {
  if (!name) return "??";
  const cleaned = String(name).replace(/[^a-zA-Z]/g, " ").trim().toUpperCase();
  if (!cleaned) return "??";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).slice(0, 2);
  return parts[0].slice(0, 2);
}

// Das Gebiet kommt als PARAMETER, nicht aus einer Modulvariablen: die Funktion
// steht ausserhalb der Komponente, ein dort abgelegter Wert froere die Sprache
// beim Import ein. Vorgabe bleibt de-DE, damit nicht angepasste Aufrufer sich
// genau wie bisher verhalten.
function formatDate(v, gebiet = "de-DE") {
  if (!v) return null;
  try {
    // SAP OData Ticks: /Date(1720000000000)/
    if (typeof v === "string" && v.startsWith("/Date(")) {
      const m = /\/Date\((\d+)\)\//.exec(v);
      if (m) return new Date(+m[1]).toLocaleDateString(gebiet);
    }
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString(gebiet);
  } catch {}
  return null;
}

export default function Scene2Package() {
  const { lang, t } = useLang();
  const gebiet = lang === "en" ? "en-GB" : "de-DE";
  const { state, dispatch } = useTransportV2();
  const { packages, loading, error, reload } = usePackageList(state.sourceInstanceId);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  // Artefakt-Popup: { pkg, loading, error, rows } | null. Die Runtime-Liste (Deploy-
  // Status der GANZEN Instanz) wird einmal geholt und wiederverwendet - sie aendert
  // sich zwischen zwei Klicks in derselben Szene praktisch nie.
  const [info, setInfo] = useState(null);
  const runtimeRef = useRef({ instId: null, map: null });

  const openInfo = async (p, e) => {
    e.stopPropagation();
    const iid = state.sourceInstanceId;
    setInfo({ pkg: p, loading: true, error: null, rows: [] });
    try {
      const flowsP = api(`/api/${iid}/content/packages/${encodeURIComponent(p.id)}/flows`, { timeout: 45000 });
      let rtP = null;
      if (runtimeRef.current.instId !== iid || !runtimeRef.current.map) {
        rtP = api(`/api/${iid}/iflows`, { timeout: 45000 }).catch(() => []);
      }
      const flows = await flowsP;
      if (rtP) {
        const rt = await rtP;
        const map = {};
        for (const r of Array.isArray(rt) ? rt : []) { if (r?.Id) map[r.Id] = r; }
        runtimeRef.current = { instId: iid, map };
      }
      const rtMap = runtimeRef.current.map || {};
      const rows = (Array.isArray(flows) ? flows : []).map((f) => {
        const rt = rtMap[f.Id];
        return {
          id: f.Id,
          name: f.Name || f.Id,
          type: f.Type || "Integration Flow",
          version: f.Version || "-",
          rtStatus: rt?.Status || null,
          rtVersion: rt?.Version || null,
        };
      }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setInfo((cur) => (cur && cur.pkg.id === p.id ? { ...cur, loading: false, rows } : cur));
    } catch (err) {
      setInfo((cur) => (cur && cur.pkg.id === p.id ? { ...cur, loading: false, error: err?.message || String(err) } : cur));
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return packages;
    const s = q.toLowerCase();
    return packages.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.id || "").toLowerCase().includes(s));
  }, [packages, q]);

  const visible = q || showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE);
  const hidden = filtered.length - visible.length;

  const pick = (p) => {
    dispatch({
      type: "PICK_PACKAGE",
      payload: { id: p.id, name: p.name, version: p.version, mod: p.mod },
    });
  };

  return (
    <section className="scene active scene-enter" id="s2">
      <div className="sh">
        <h2>{t("s2.h2")}</h2>
        <p>{t("s2.p")}</p>
      </div>

      <div className="searchrow">
        <div className="search-wrap">
          <svg className="ic ic-16"><use href="#ic-search" /></svg>
          <input
            className="inp"
            type="search"
            placeholder={t("s2.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="tiny muted">
          {state.pkgId ? t("s2.sel", { name: state.pkgName || state.pkgId }) : ""}
        </span>
      </div>

      {loading && <Skeleton variant="cards" count={8} columns={4} label={t("common.loading")} />}
      {error && (
        <div className="errline">
          <span style={{ flex: 1 }}>{t("err.packages")}: {error}</span>
          <button className="btn btn-sec btn-sm" onClick={reload}>{t("err.retry")}</button>
        </div>
      )}

      <div className={"pkg-grid" + (state.pkgId ? " has-selection" : "")}>
        {visible.map((p, i) => (
          <button
            key={p.id}
            type="button"
            className={"pkg-card" + (state.pkgId === p.id ? " selected" : "") + " stagger-in"}
            style={{ "--i": i }}
            onClick={() => pick(p)}
          >
            <span className="row1">
              <span className="pkg-mark">{pkgMark(p.name)}</span>
              <span className="pkg-name">{p.name}</span>
              <span className="card-check">
                <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path className="check-path" d="M5 12.5 L10 17.5 L19 6.5" />
                </svg>
              </span>
            </span>
            <span className="pkg-id">{p.id}{p.version ? ` · ${t("s2.ver", { v: p.version })}` : ""}</span>
            <span className="pkg-meta">
              {p.shortText && <span>{p.shortText}</span>}
              {formatDate(p.mod, gebiet) && <span>{t("s2.mod", { d: formatDate(p.mod, gebiet) })}</span>}
            </span>
            {/* Ecklasche oben rechts (gleiche Bauart wie die CI/CD-Lasche der Space-Karten).
                span statt button: die Kachel selbst IST ein button, verschachtelte Buttons
                sind invalides HTML. Enter/Leertaste oeffnen trotzdem. */}
            <span
              role="button"
              tabIndex={0}
              className="pkg-info-btn"
              title={t("s2.infoBtn")}
              aria-label={t("s2.infoBtn")}
              onClick={(e) => openInfo(p, e)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openInfo(p, e); } }}
            >
              <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
              </svg>
            </span>
          </button>
        ))}
        {!loading && !error && filtered.length === 0 && (
          <span className="muted">{t("s2.none")}</span>
        )}
      </div>

      {hidden > 0 && (
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-sec btn-sm" onClick={() => setShowAll(true)}>
            {t("s2.more", { n: hidden })}
          </button>
        </div>
      )}

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 1 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <button className="btn btn-cta" onClick={() => dispatch({ type: "SET_STEP", payload: 3 })} disabled={!state.pkgId}>
          {t("s2.next")}
          <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
        </button>
      </div>

      {/* Artefakt-Popup nativ als .modal-ov (kein MUI-Portal). */}
      <div
        className={"modal-ov" + (info ? " show" : "")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="s2-info-title"
        onClick={(e) => { if (e.target === e.currentTarget) setInfo(null); }}
      >
        <div className="modal" style={{ maxWidth: 860 }}>
          <div className="modal-h">
            <div style={{ minWidth: 0 }}>
              <h3 id="s2-info-title" style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {info ? t("s2.infoTitle", { name: info.pkg.name }) : ""}
              </h3>
              {info && !info.loading && !info.error && (
                <span className="tiny muted">
                  {t("s2.infoSub", { n: info.rows.length, m: info.rows.filter((r) => r.rtStatus === "STARTED").length })}
                </span>
              )}
            </div>
            <button type="button" className="modal-x" aria-label={t("common.close")} onClick={() => setInfo(null)}>
              <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6 L18 18 M18 6 L6 18" />
              </svg>
            </button>
          </div>
          <div className="modal-b">
            {info?.loading && <div className="muted" style={{ padding: "8px 0" }}>{t("s2.infoLoading")}</div>}
            {info?.error && <div className="errline"><span style={{ flex: 1 }}>{t("s2.infoErr")}: {info.error}</span></div>}
            {info && !info.loading && !info.error && info.rows.length === 0 && (
              <div className="muted" style={{ padding: "8px 0" }}>{t("s2.infoNone")}</div>
            )}
            {info && !info.loading && !info.error && info.rows.length > 0 && (
              <table className="dt dt--compact">
                <thead>
                  <tr>
                    <th>{t("th.art")}</th>
                    <th>{t("th.type")}</th>
                    <th>{t("s2.thVer")}</th>
                    <th>{t("th.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {info.rows.map((r) => {
                    // STARTED = laeuft im Ziel; jeder andere Runtime-Status (ERROR,
                    // STARTING, STOPPING) wird woertlich gezeigt statt beschoenigt.
                    const deployedOk = r.rtStatus === "STARTED";
                    const versionAbweichend = deployedOk && r.rtVersion && r.rtVersion !== r.version;
                    return (
                      <tr key={r.id}>
                        <td>
                          <span className="art-name">{r.name}</span>
                          {/* Id nur zeigen, wenn sie vom Namen abweicht - identische
                              Doppelzeilen kosteten die halbe Fensterhoehe. */}
                          {r.id !== r.name && <><br /><span className="art-id">{r.id}</span></>}
                        </td>
                        <td>{r.type}</td>
                        <td>{r.version}</td>
                        <td>
                          {deployedOk && (
                            <span className="st st--ok"><i className="dot" />
                              {versionAbweichend ? t("s2.deployedOther", { v: r.rtVersion }) : t("s2.deployed")}
                            </span>
                          )}
                          {!deployedOk && r.rtStatus && (
                            <span className="st st--crit"><i className="dot" />{r.rtStatus}</span>
                          )}
                          {!r.rtStatus && (
                            <span className="st st--idle"><i className="dot" />{t("s2.notDeployed")}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
